import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DateTimePicker } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccessAlarmsRoundedIcon from "@mui/icons-material/AccessAlarmsRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useDialog } from "../../../../components/DialogContext";
import { pageScrollStyle } from "../../../../styles/styles";
import {
  createTask,
  getAllTask,
  getAllTaskType,
  updateTaskOrType,
} from "../../../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";
import {
  getCases,
  getPracticeArea,
} from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../../static/server";
import UserApiField from "../../../../components/LawyerCompo/Settings/FieldsUi/UserApiField";
import DrawerTemplate from "../../../../components/LawyerCompo/Cases/DrawerTemplate";
import { useAccess } from "../../../../components/Auth/AccessProvider";
import ContactApiField from "../../../../components/LawyerCompo/Settings/FieldsUi/ContactApiField";
import MatterApiField from "../../../../components/LawyerCompo/Settings/FieldsUi/MatterApiField";

const CreateTaskForm = ({
  isEditMode,
  editTaskId,
  formData,
  sourceTemplate,
  isBulkEdit = false,
  selectedIds = [],
  selectedRows = [],
  onSubmit: externalSubmit,
}) => {
  const dispatch = useDispatch();
  const { showDialog, closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAssignTo, setSelectedAssignTo] = useState(null);
  const [disableAssignMatter, setDisableAssignMatter] = useState(false);
  const [selected, setSelected] = useState("case");

  const { hasPageAccess } = useAccess();
  const { token, teamMemberLawyerId, lawerId } = useSelector(
    (state) => state.user,
  );

  const editClientData =
    isEditMode && formData
      ? (formData?.row?.full_data || formData)?.client
      : null;
  const editCaseData =
    isEditMode && formData
      ? (formData?.row?.full_data || formData)?.case
      : null;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      task_name: "",
      priority: isBulkEdit ? "" : "normal",
      comment: "",
      client: null,
      assign_to: null,
      private_task: false,
      task_type: "",
      status: isBulkEdit ? "" : "Open",
      estimate_time: null,
      case: null,
      closed_on: "",
      task_alarm: [],
      is_practice_area: true,
      is_people: false,
      is_company: false,

      // hour: "",
      // extra_hour_amount: "",
      // total: "",
      // fee_cap: "",
      // expense_cap: "",
      // seprate_billing: false,
      // is_dependent: false,
      // reference_date: null,
      // respond_by: null,
    },
  });

  useEffect(() => {
    dispatch(getCases({ url: `${backend_api}case/register/`, token }));
    dispatch(
      getAllTaskType({
        url: `${backend_api}task/register/`,
        token,
        teamMemberLawyerId: teamMemberLawyerId,
      }),
    );
    dispatch(
      getAllTask({
        url: `${backend_api}task/task-register/`,
        token,
        teamMemberLawyerId: teamMemberLawyerId,
      }),
    );
    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      }),
    );
  }, [dispatch, teamMemberLawyerId, token]);

  useEffect(() => {
    if (isEditMode && formData) {
      const data = formData?.row?.full_data || formData;
      if (data?.assign_to?.id === lawerId) {
        setDisableAssignMatter(true);
      }
    }
  }, [isEditMode, formData, lawerId]);

  useEffect(() => {
    if (!isEditMode || !formData) return;

    const data = formData?.row?.full_data || formData;

    // Basic fields
    setValue("task_name", data?.task_name || "");
    setValue("priority", data?.priority?.toLowerCase() || "normal");
    setValue("comment", data?.comment || data?.description || "");
    setValue("private_task", Boolean(data?.private_task));

    // Client handling - Fix for relates to selection
    if (data?.client?.id) {
      setValue("client", data.client.id);
    } else if (data?.client_id) {
      setValue("client", data.client_id);
    }

    // Status mapping - Fix for task status
    const statusMapping = {
      Open: "Open",
      Pending: "Open",
      Transfered: "Transfered",
      "In Progress": "Transfered",
      Reviewed: "Reviewed",
      "In Review": "Reviewed",
      Completed: "Completed",
      Complete: "Completed",
    };
    setValue("status", statusMapping[data?.status] || data?.status || "Open");

    // Estimate time handling - Fix for estimate time error
    if (data?.estimate_time && data.estimate_time !== "-") {
      try {
        const estimateTime = dayjs(data.estimate_time);
        if (estimateTime.isValid()) {
          setValue("estimate_time", estimateTime);
        }
      } catch (error) {
        console.warn("Invalid estimate_time format:", data.estimate_time);
      }
    }

    // Reference date handling
    if (data?.reference_date) {
      setValue("reference_date", dayjs(data.reference_date));
    }

    // Respond by date handling
    if (data?.respond_by) {
      setValue("respond_by", dayjs(data.respond_by));
    }

    // Due date handling
    if (data?.closed_on) {
      setValue("closed_on", dayjs(data.closed_on).format("YYYY-MM-DD"));
    } else if (formData?.due_date) {
      setValue("closed_on", dayjs(formData.due_date).format("YYYY-MM-DD"));
    }

    // Case handling
    if (data?.case && data?.case !== "-") {
      const caseId = typeof data.case === "object" ? data.case.id : data.case;
      setValue("case", caseId);
    }

    // Assign to handling
    if (data?.assign_to) {
      setSelectedAssignTo(data.assign_to);
      setValue("assign_to", data.assign_to.id);
    }

    // Task alarm handling - Fix for reminders not showing
    if (Array.isArray(data?.task_alarm) && data.task_alarm.length > 0) {
      const formattedAlarms = data.task_alarm.map((alarm) => ({
        via: alarm.via || "email",
        alram_at: alarm.alram_at ? dayjs(alarm.alram_at) : dayjs(),
      }));
      setValue("task_alarm", formattedAlarms);
    }

    if (data?.case && data?.case !== "-") {
      setSelected("case");
      setValue("is_practice_area", false);
      setValue("is_people", false);
      setValue("is_company", false);

      const caseId = typeof data.case === "object" ? data.case.id : data.case;
      setValue("case", caseId);
    } else if (data?.is_company === true) {
      setSelected("is_company");
      setValue("is_practice_area", false);
      setValue("is_people", false);
      setValue("is_company", true);

      if (data?.client?.id) {
        setValue("client", data.client.id);
      } else if (data?.client_id) {
        setValue("client", data.client_id);
      }
    } else if (data?.is_people === true) {
      setSelected("is_people");
      setValue("is_practice_area", false);
      setValue("is_people", true);
      setValue("is_company", false);

      if (data?.client?.id) {
        setValue("client", data.client.id);
      } else if (data?.client_id) {
        setValue("client", data.client_id);
      }
    } else {
      setSelected("is_practice_area");
      setValue("is_practice_area", true);
      setValue("is_people", false);
      setValue("is_company", false);
    }

    // console.log("Form data being processed:", data);
  }, [isEditMode, formData, setValue]);

  useEffect(() => {
    if (isEditMode || isBulkEdit || !sourceTemplate) return;

    const data = sourceTemplate?.row?.full_data || sourceTemplate;

    setValue("task_name", data?.task_name || "");
    setValue("priority", data?.priority?.toLowerCase() || "normal");
    setValue("comment", data?.comment || data?.description || "");
    setValue("private_task", Boolean(data?.private_task));
    setValue("status", data?.status || "Open");
    setValue("assign_to", null);
    setSelectedAssignTo(null);
    setValue("case", null);
    setValue("client", null);

    if (Array.isArray(data?.task_alarm) && data.task_alarm.length > 0) {
      const formattedAlarms = data.task_alarm.map((alarm) => ({
        via: alarm.via || "email",
        alram_at: alarm.alram_at ? dayjs(alarm.alram_at) : dayjs(),
      }));
      setValue("task_alarm", formattedAlarms);
    } else {
      setValue("task_alarm", []);
    }

    if (data?.is_people === true) {
      setSelected("is_people");
      setValue("is_practice_area", false);
      setValue("is_people", true);
      setValue("is_company", false);
      return;
    }

    if (data?.is_company === true) {
      setSelected("is_company");
      setValue("is_practice_area", false);
      setValue("is_people", false);
      setValue("is_company", true);
      return;
    }

    // Practice-area templates need a matter to become a real task, so we
    // seed the form in "Matter" mode and let the user pick the target case.
    setSelected("case");
    setValue("is_practice_area", false);
    setValue("is_people", false);
    setValue("is_company", false);
  }, [isBulkEdit, isEditMode, setValue, sourceTemplate]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "task_alarm",
    keyName: "id",
  });

  const privateTaskValue = watch("private_task");

  useEffect(() => {
    if (!privateTaskValue) return;

    setValue("assign_to", null, { shouldDirty: true, shouldValidate: true });
    setSelectedAssignTo(null);
  }, [privateTaskValue, setValue]);

  const handleChange = (event) => {
    const value = event.target.value;
    setSelected(value);

    setValue("is_practice_area", value === "is_practice_area");
    setValue("is_people", value === "is_people");
    setValue("is_company", value === "is_company");

    if (value === "case") {
      setValue("client", null);
    } else {
      setValue("case", null);
    }
  };

  const cleanFormData = (data) => {
    const cleanedData = {};

    const booleanFields = [
      "private_task",
      "seprate_billing",
      "is_dependent",
      "is_practice_area",
      "is_people",
      "is_company",
    ];

    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (booleanFields.includes(key)) {
        cleanedData[key] = Boolean(value);
        return;
      }

      if (Array.isArray(value)) {
        if (value.length > 0) {
          const cleanedArray = value
            .map((item) => {
              if (typeof item === "object" && item !== null) {
                const cleanedItem = {};
                Object.keys(item).forEach((itemKey) => {
                  const itemValue = item[itemKey];
                  if (
                    itemValue !== null &&
                    itemValue !== undefined &&
                    itemValue !== ""
                  ) {
                    if (dayjs.isDayjs(itemValue)) {
                      cleanedItem[itemKey] = itemValue.toISOString();
                    } else {
                      cleanedItem[itemKey] = itemValue;
                    }
                  }
                });
                return Object.keys(cleanedItem).length > 0 ? cleanedItem : null;
              }
              return item;
            })
            .filter((item) => item !== null);

          if (cleanedArray.length > 0) {
            cleanedData[key] = cleanedArray;
          }
        }
        return;
      }

      if (dayjs.isDayjs(value)) {
        cleanedData[key] = value.toISOString();
        return;
      }

      if (value !== null && value !== undefined && value !== "") {
        if (typeof value === "number" || !isNaN(value)) {
          cleanedData[key] = value;
        } else {
          cleanedData[key] = value;
        }
      }
    });

    return cleanedData;
  };

  const onSubmit = async (data) => {
    if (isBulkEdit && externalSubmit) {
      const cleanedData = cleanFormData(data);
      delete cleanedData.id;
      delete cleanedData.created_at;
      delete cleanedData.updated_at;
      await externalSubmit(cleanedData);
      return;
    }
    setIsSubmitting(true);

    try {
      const cleanedData = cleanFormData(data);

      if (selected === "case") {
        delete cleanedData.client;
      } else if (selected === "is_people" || selected === "is_company") {
        delete cleanedData.case;
      }

      if (cleanedData.private_task) {
        delete cleanedData.assign_to;
      }

      if (isEditMode) {
        await dispatch(
          updateTaskOrType({
            url: `${backend_api}task/task-register/${editTaskId}/`,
            body: cleanedData,
            token,
            teamMemberLawyerId,
          }),
        ).unwrap();
        toast.success("Task updated successfully");
      } else {
        await dispatch(
          createTask({
            url: `${backend_api}task/task-register/`,
            body: cleanedData,
            token,
            teamMemberLawyerId,
          }),
        ).unwrap();
        toast.success("Task created successfully!");
      }

      dispatch(
        getAllTask({
          url: `${backend_api}task/task-register/`,
          token,
          teamMemberLawyerId: teamMemberLawyerId,
        }),
      );

      closeDialog();
      reset();
    } catch (error) {
      toast.error(
        error?.message ||
          (isEditMode ? "Failed to update task" : "Failed to create task"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAlarm = () => {
    append({
      via: "email",
      alram_at: dayjs(),
    });
  };

  return (
    <Box>
      {isBulkEdit && (
        <Alert severity="info" sx={{ m: 2 }}>
          You are editing {selectedIds.length} items. Changes will apply to all
          selected tasks.
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            p: "1rem",
          }}
        >
          {/* Task Name and Priority */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            {!isBulkEdit && (
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="task_name"
                  control={control}
                  rules={{
                    required: !isBulkEdit ? "Task name is required" : false,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label={
                        <span>
                          Task Name <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      fullWidth
                      error={Boolean(errors.task_name)}
                      helperText={errors.task_name?.message}
                    />
                  )}
                />
              </Box>
            )}
            <Box sx={{ flex: 0.5 }}>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="priority-label">Priority</InputLabel>
                    <Select
                      {...field}
                      labelId="priority-label"
                      label="Priority"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </Box>

          {/* People/Company Selection */}
          {!isBulkEdit && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Relates to
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                {[
                  { key: "case", label: "Matter" },
                  { key: "is_people", label: "People" },
                  { key: "is_company", label: "Company" },
                ].map((opt, index) => (
                  <motion.div
                    key={opt.key}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Chip
                      label={
                        <FormControlLabel
                          value={opt.key}
                          control={
                            <Radio
                              checked={selected === opt.key}
                              onChange={handleChange}
                              value={opt.key}
                            />
                          }
                          label={opt.label}
                        />
                      }
                      variant="outlined"
                      sx={{
                        transition: "all 0.3s ease",
                      }}
                    />
                  </motion.div>
                ))}
              </Box>

              {selected === "is_people" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Box sx={{ mt: 2 }}>
                    <ContactApiField
                      name="client"
                      label="Person"
                      control={control}
                      token={token}
                      apiUrl={`${backend_api}contact/retrieve/?contact_type=person`}
                      isRequired={true}
                      errors={errors}
                      placeholder="Select person"
                      returnFullData={false}
                      editData={editClientData}
                    />
                  </Box>
                </motion.div>
              )}

              {selected === "is_company" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Box sx={{ mt: 2 }}>
                    <ContactApiField
                      name="client"
                      label="Company"
                      control={control}
                      token={token}
                      apiUrl={`${backend_api}contact/retrieve/?contact_type=company`}
                      isRequired={true}
                      errors={errors}
                      placeholder="Select company"
                      returnFullData={false}
                      isCompany={true}
                      editData={editClientData}
                    />
                  </Box>
                </motion.div>
              )}

              {selected === "case" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Box sx={{ mt: 2 }}>
                    <Controller
                      name="case"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <MatterApiField
                          name="case"
                          value={value}
                          onChange={onChange}
                          token={token}
                          errors={errors}
                          apiUrl={`${backend_api}case/register/`}
                          placeholder="Select matter"
                          AddMatterFunc={({ onCreated }) =>
                            showDialog(
                              <DrawerTemplate onMatterCreated={onCreated} />,
                              {
                                dialogTitle: "Select Template",
                              },
                            )
                          }
                          disabled={disableAssignMatter}
                          editData={
                            typeof editCaseData === "object"
                              ? editCaseData
                              : null
                          }
                        />
                      )}
                    />
                  </Box>
                </motion.div>
              )}
            </Box>
          )}

          {/* Assignment and Private Task */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Box sx={{ flex: 1 }} id="access_assign_to_other">
              {hasPageAccess("access_assign_to_other") ? (
                <UserApiField
                  name="assign_to"
                  label="Assign To"
                  control={control}
                  errors={errors}
                  isRequired={false}
                  apiUrl={`${backend_api}team/create-employee/?is_active=True`}
                  showSelfOption={true}
                  selfOptionLabel="Me (Self)"
                  defaultUser={selectedAssignTo}
                  disabled={disableAssignMatter || privateTaskValue}
                  teamMemberLawyerId={teamMemberLawyerId}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: "text.disabled" }}
                >
                  You don't have access
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 0.5 }}>
              <Box
                id="access_private_task_input"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: `1px solid ${
                    privateTaskValue ? "#00B3B0" : "#c5c5c7"
                  }`,
                  borderRadius: "0.2rem",
                  px: "0.5rem",
                  height: "40px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "0.8rem", fontWeight: "500" }}
                >
                  {privateTaskValue ? "Private task" : "Visible Task"}
                </Typography>
                <Controller
                  name="private_task"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={Boolean(value)}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Task Type */}
          <Box>
            {/* <TaskApiField
              name="task_type"
              label="Task Type"
              control={control}
              errors={errors}
              token={token}
              apiUrl={`${backend_api}task/register/`}
              placeholder="Select task type"
              TaskTypeFormComponent={CreateTaskTypeForm}
              createTaskTypeDialogTitle="Add Task Type"
              editData={allTaskType?.find((t) => t.id === watch("task_type"))}
              renderNoOptions={
                <NoDataOptionField
                  imgSrc={EmptyList}
                  text={"Don't have any Task Type"}
                  btnText={"Task type"}
                  handleClickNoData={() =>
                    showDialog(<CreateTaskTypeForm />, {
                      dialogTitle: "Add Task Type",
                    })
                  }
                />
              }
            /> */}
          </Box>

          {/* Task Status and Estimate Time */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="status"
              control={control}
              rules={{
                required: "Task status is required",
              }}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel id="task_status-label">Task Status</InputLabel>
                  <Select
                    {...field}
                    labelId="task_status-label"
                    label="Task Status"
                  >
                    <MenuItem value="Open">Pending</MenuItem>
                    <MenuItem value="Transfered">In Progress</MenuItem>
                    <MenuItem value="Reviewed">In Review</MenuItem>
                    <MenuItem value="Completed">Complete</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            {!isBulkEdit && (
              <Controller
                name="estimate_time"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => field.onChange(newValue)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: Boolean(errors.estimate_time),
                        helperText: errors.estimate_time?.message,
                      },
                    }}
                    label="Estimate Time"
                    format="MM/DD/YYYY hh:mm a"
                  />
                )}
              />
            )}
          </Box>

          {/* Due Date */}
          <Box>
            <Controller
              name="closed_on"
              control={control}
              rules={{
                required: "Due Date is required",
              }}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    const formattedValue = newValue
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : null;
                    field.onChange(formattedValue);
                  }}
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      label: "Due Date",
                      size: "small",
                      variant: "outlined",
                      error: Boolean(errors.closed_on),
                      helperText: errors.closed_on?.message,
                    },
                    popper: {
                      sx: {
                        "& .MuiPickersPopper-root": {
                          maxWidth: "100%",
                          maxHeight: "100%",
                          overflow: "hidden",
                        },
                        "& .MuiPaper-root": {
                          width: "fit-content",
                          maxHeight: "15rem",
                          overflowY: "auto",
                          ...pageScrollStyle,
                        },
                      },
                    },
                  }}
                  format="YYYY-MM-DD"
                />
              )}
            />
          </Box>

          {/* Reference Date and Respond By
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="reference_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    const formattedValue = newValue
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : null;
                    field.onChange(formattedValue);
                  }}
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      label: "Reference Date",
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                  format="YYYY-MM-DD"
                />
              )}
            />

            <Controller
              name="respond_by"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    const formattedValue = newValue
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : null;
                    field.onChange(formattedValue);
                  }}
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      label: "Respond By",
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                  minDate={dayjs()}
                  format="YYYY-MM-DD"
                />
              )}
            />
          </Box> */}

          {/* Task Complexity */}
          {/* <Box>
            <Controller
              name="task_complexity"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel id="complexity-label">Task Complexity</InputLabel>
                  <Select
                    {...field}
                    labelId="complexity-label"
                    label="Task Complexity"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box> */}

          {/* Budget and Hours */}
          {/* <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="budget"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Budget"
                  fullWidth
                  type="number"
                />
              )}
            />

            <Controller
              name="hour"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Hours"
                  fullWidth
                  type="number"
                />
              )}
            />
          </Box> */}

          {/* Extra Hour Amount and Total */}
          {/* <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="extra_hour_amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Extra Hour Amount"
                  fullWidth
                  type="number"
                />
              )}
            />

            <Controller
              name="total"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Total"
                  fullWidth
                  type="number"
                />
              )}
            />
          </Box> */}

          {/* Fee Cap and Expense Cap */}
          {/* <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="fee_cap"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Fee Cap"
                  fullWidth
                  type="number"
                />
              )}
            />

            <Controller
              name="expense_cap"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Expense Cap"
                  fullWidth
                  type="number"
                />
              )}
            />
          </Box> */}

          {/* Separate Billing and Dependent Task */}
          {/* <Box sx={{ display: "flex", gap: "1rem" }}>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #c5c5c7",
                  borderRadius: "0.2rem",
                  px: "0.5rem",
                  height: "40px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "0.8rem", fontWeight: "500" }}
                >
                  Separate Billing
                </Typography>
                <Controller
                  name="seprate_billing"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={Boolean(value)}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #c5c5c7",
                  borderRadius: "0.2rem",
                  px: "0.5rem",
                  height: "40px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "0.8rem", fontWeight: "500" }}
                >
                  Dependent Task
                </Typography>
                <Controller
                  name="is_dependent"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={Boolean(value)}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box> */}

          {/* Description */}
          {!isBulkEdit && (
            <Box>
              <Controller
                name="comment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    rows={3}
                    fullWidth
                    label="Description"
                  />
                )}
              />
            </Box>
          )}

          <Divider orientation="horizontal" flexItem />

          {/* Reminders Section */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight="600">
                Reminders
              </Typography>
              {fields.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  onClick={handleAddAlarm}
                  sx={{ ml: "auto" }}
                >
                  Add Reminder
                </Button>
              )}
            </Box>

            {fields.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {fields.map((field, index) => (
                  <Box
                    key={field.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      p: "1rem",
                      border: "1px solid #e0e0e0",
                      borderRadius: "0.5rem",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Controller
                      name={`task_alarm.${index}.via`}
                      control={control}
                      defaultValue={field.via}
                      render={({ field }) => (
                        <Select {...field} size="small" sx={{ flex: 1 }}>
                          <MenuItem value="email">Me (via Email)</MenuItem>
                          <MenuItem value="notification">
                            Me (via Notification)
                          </MenuItem>
                        </Select>
                      )}
                    />

                    <Controller
                      name={`task_alarm.${index}.alram_at`}
                      control={control}
                      defaultValue={field.alram_at}
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { width: "16rem" },
                            },
                          }}
                          minDate={dayjs()}
                          format="MM/DD/YYYY hh:mm a"
                        />
                      )}
                    />

                    <IconButton
                      onClick={() => remove(index)}
                      aria-label="Remove alarm"
                      size="small"
                      sx={{
                        color: "error.main",
                        "&:hover": {
                          backgroundColor: "error.light",
                          color: "white",
                        },
                      }}
                    >
                      <CloseRoundedIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  my: 2,
                  p: "2rem",
                  border: "2px dashed #e0e0e0",
                  borderRadius: "0.5rem",
                  backgroundColor: "#fafafa",
                }}
              >
                <AccessAlarmsRoundedIcon
                  sx={{
                    fontSize: "4rem",
                    color: "primary.main",
                    opacity: "0.5",
                  }}
                />
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    No reminders set for this task
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Add reminders to get notified about this task via email or
                    notification
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    onClick={handleAddAlarm}
                  >
                    Add First Reminder
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Form Footer/Actions */}
        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
            bgcolor: "rgba(255, 255, 255, 0.8)",
          }}
        >
          <Button variant="outlined" size="small" onClick={closeDialog}>
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            variant="contained"
            color="primary"
            type="submit"
            size="small"
          >
            {isEditMode
              ? "Update Task"
              : isBulkEdit
                ? "Edit Tasks"
                : sourceTemplate
                  ? "Create Task From Template"
                  : "Create Task"}
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default CreateTaskForm;
